import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { InternetPlan } from '../types';
import PlanConnectModal from './PlanConnectModal';
import { LeadContext, buildCheckServiceUrl, loadLeadContext } from '../utils/leadContext';

type HomeKingmanPlansProps = {
  lead?: LeadContext | null;
  /** No solid section fill — for scroll-over hero draft layouts */
  transparentBg?: boolean;
};

const formatSpeedPart = (raw: string) => {
  let part = raw.trim();
  if (/^1\.?0?\s*g(bps)?$/i.test(part) || /^1g$/i.test(part)) return '1 Gbps';
  if (/^2\.5\s*g(bps)?$/i.test(part) || /^2\.5g$/i.test(part)) return '2.5 Gbps';
  if (/gbps|\bg\b/i.test(part)) return part.replace(/\s+/g, ' ');
  part = part.replace(/\s*mbps\s*/i, '').trim();
  return part;
};

/** Single hero speed line like "500 Mbps" or "100–50 Mbps". */
const getHeroSpeed = (plan: InternetPlan) => {
  if (!plan.speed.includes('/')) {
    const single = formatSpeedPart(plan.speed);
    if (/gbps/i.test(single)) return single;
    if (/mbps/i.test(plan.speed) || /^\d/.test(single)) {
      return /mbps|gbps/i.test(single) ? single : `${single} Mbps`;
    }
    return plan.speed;
  }

  const [rawDown, ...rest] = plan.speed.split('/');
  const rawUp = rest.join('/').trim();
  const down = formatSpeedPart(rawDown);
  const up = formatSpeedPart(rawUp.replace(/\s*mbps\s*/i, '').trim() || rawUp);

  const downCore = down.replace(/\s*mbps|\s*gbps/gi, '').trim();
  const upCore = up.replace(/\s*mbps|\s*gbps/gi, '').trim();

  if (downCore.toLowerCase() === upCore.toLowerCase()) {
    if (/gbps/i.test(down) || /g$/i.test(downCore)) {
      return /gbps/i.test(down) ? down : `${downCore} Gbps`;
    }
    return `${downCore} Mbps`;
  }

  const unit = /gbps/i.test(down) || /gbps/i.test(up) ? 'Gbps' : 'Mbps';
  // STARTER-style ranges: show "100-300 / 50 Mbps"
  if (downCore.includes('-') || upCore.includes('-')) {
    return `${downCore} / ${upCore} ${unit}`;
  }
  return `${downCore}–${upCore} ${unit}`;
};

const HomeKingmanPlans: React.FC<HomeKingmanPlansProps> = ({ lead: leadProp, transparentBg = false }) => {
  const [plans, setPlans] = useState<InternetPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerType, setCustomerType] = useState<'residential' | 'business'>('residential');
  const [lead, setLead] = useState<LeadContext | null>(leadProp ?? null);
  const [connectPlan, setConnectPlan] = useState<InternetPlan | null>(null);
  const [openAddons, setOpenAddons] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (leadProp !== undefined) {
      setLead(leadProp);
    }
  }, [leadProp]);

  useEffect(() => {
    if (leadProp === undefined) {
      const saved = loadLeadContext();
      if (saved?.available) setLead(saved);
    }
  }, [leadProp]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansData = await apiService.getPlans();
        setPlans(plansData);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const isBusinessPlan = (plan: InternetPlan) =>
    plan.category === 'business' || plan.name === 'Business PRO';
  const starterPlan = plans.find((plan) => plan.name === 'STARTER');
  const fiberPlans = [
    ...(starterPlan ? [starterPlan] : []),
    ...plans.filter((plan) => plan.category === 'fiber' && !isBusinessPlan(plan)),
  ];
  const businessPlans = plans
    .filter((plan) => isBusinessPlan(plan))
    .sort((a, b) => a.price - b.price);
  const activeAudiencePlans = customerType === 'business' ? businessPlans : fiberPlans;

  const handleGetConnected = (plan: InternetPlan) => {
    if (lead?.available && lead.lat && lead.lng && lead.address) {
      const url = buildCheckServiceUrl({
        plan: { id: plan.id, name: plan.name, price: plan.price, speed: plan.speed },
        lead,
      });
      navigate(url, {
        state: {
          selectedPlan: plan.id,
          selectedPlanName: plan.name,
          selectedPlanPrice: plan.price,
          selectedPlanSpeed: plan.speed,
          leadContext: lead,
        },
      });
      window.scrollTo(0, 0);
      return;
    }
    setConnectPlan(plan);
  };

  const renderPlanCard = (plan: InternetPlan, index: number) => {
    const coreFeatures = plan.features.filter((f) => !f.includes('(Optional Add-on)'));
    const addons = plan.features.filter((f) => f.includes('(Optional Add-on)'));
    const addonsOpen = openAddons === plan.id;
    const priceLabel = Number.isInteger(plan.price) ? plan.price.toFixed(0) : plan.price.toFixed(2);

    return (
      <motion.div
        key={plan.id}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.08 }}
        className={`relative flex flex-col rounded-2xl border p-6 md:p-7 mobile-plan-card ${
          transparentBg
            ? 'bg-black/40 backdrop-blur-md border-white/15'
            : 'bg-[#0a0f1a]/80 backdrop-blur-sm border-white/10'
        } ${
          plan.popular
            ? 'border-blue-500 shadow-[0_0_28px_rgba(37,99,235,0.45)] ring-1 ring-blue-500/80'
            : 'shadow-lg shadow-black/25'
        }`}
      >
        {plan.popular && (
          <div className="absolute -top-[1px] left-1/2 -translate-x-1/2 z-10">
            <span
              className="inline-block bg-blue-600 text-white px-5 pt-1.5 pb-1.5 text-[11px] font-semibold whitespace-nowrap leading-none"
              style={{
                clipPath: 'polygon(0 0, 100% 0, 92% 100%, 8% 100%)',
                borderRadius: '0 0 4px 4px',
              }}
            >
              Most Popular
            </span>
          </div>
        )}

        <div className="text-center pt-1">
          <h3 className="text-sm font-bold tracking-[0.14em] uppercase text-white">
            {plan.name}
          </h3>
          <p className="mt-3 text-3xl md:text-[2rem] font-extrabold text-blue-500 leading-none">
            {getHeroSpeed(plan)}
          </p>
          <p className="mt-1.5 text-xs text-white/55">Download &amp; Upload</p>
          <p className="mt-4 text-[11px] font-bold tracking-[0.16em] uppercase text-sky-400">
            Lifetime Price Lock
          </p>
          <p className="mt-2 text-3xl font-bold text-white mobile-plan-price">
            ${priceLabel}
            <span className="text-base font-medium text-white/70">/month</span>
          </p>
          {plan.taxesApply && (
            <p className="text-xs text-white/40 mt-1">+ tax</p>
          )}
        </div>

        <ul className="mt-6 space-y-2.5 flex-1 mobile-plan-features">
          {coreFeatures.map((feature, featureIndex) => (
            <li key={featureIndex} className="flex items-start gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-white/85 text-sm leading-snug">{feature}</span>
            </li>
          ))}
        </ul>

        {addons.length > 0 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setOpenAddons(addonsOpen ? null : plan.id)}
              className="text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              View add-ons {addonsOpen ? '−' : '+'}
            </button>
            {addonsOpen && (
              <ul className="mt-2 space-y-1.5">
                {addons.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-2 text-sm text-white/60">
                    <span className="text-blue-400 mt-0.5">+</span>
                    <span>{feature.replace(' (Optional Add-on)', '')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => handleGetConnected(plan)}
          className="mt-6 w-full text-center py-3 px-4 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-500 transition-all"
        >
          Get Connected
        </button>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <section id="home-plans" className={`py-20 ${transparentBg ? 'bg-transparent' : 'bg-[#111827]'}`}>
        <div className="flex items-center justify-center min-h-[320px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      </section>
    );
  }

  return (
    <>
      {lead?.available && (
        <div className="bg-green-500/15 border-b border-green-400/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm text-green-200">
            <strong>Service available</strong> at {lead.formatted || `${lead.address}, ${lead.city}`}
            {' — '}pick a plan below, then leave your contact info.
          </div>
        </div>
      )}

      <section id="home-plans" className={`py-14 md:py-16 ${transparentBg ? 'bg-transparent' : 'bg-[#111827]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2
              className={`text-3xl md:text-4xl font-bold text-white mb-3 ${
                transparentBg ? 'drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]' : ''
              }`}
            >
              Internet Plans
            </h2>
            <p
              className={`text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 ${
                transparentBg ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]' : ''
              }`}
            >
              Fast, symmetrical speeds with no contracts, unlimited data, and local support.
            </p>

            <div className="mb-2">
              <div className="inline-flex items-center bg-black/35 backdrop-blur-sm rounded-full p-1 border border-white/10">
                <button
                  type="button"
                  onClick={() => setCustomerType('residential')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    customerType === 'residential'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  Residential
                </button>
                <button
                  type="button"
                  onClick={() => setCustomerType('business')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    customerType === 'business'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  Business
                </button>
              </div>
            </div>
          </div>

          {customerType === 'business' && (
            <p className="text-center text-white/65 max-w-3xl mx-auto mb-8">
              Business packages include <strong className="text-white">Bizz Support</strong> — priority help from our
              Arizona business team when you need it.
            </p>
          )}

          {activeAudiencePlans.length > 0 ? (
            <div
              className={
                customerType === 'business'
                  ? `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto ${
                      activeAudiencePlans.length === 1 ? 'max-w-md' : ''
                    }`
                  : `grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6 ${
                      activeAudiencePlans.length === 1
                        ? 'lg:grid-cols-1 max-w-md mx-auto'
                        : activeAudiencePlans.length > 4
                          ? 'lg:grid-cols-3 xl:grid-cols-4'
                          : 'lg:grid-cols-4'
                    }`
              }
            >
              {activeAudiencePlans.map((plan, index) => renderPlanCard(plan, index))}
            </div>
          ) : (
            <p className="text-center text-white/60 max-w-xl mx-auto">
              {customerType === 'business'
                ? 'Business packages coming soon. Call '
                : 'Residential packages coming soon. Call '}
              <a href="tel:9285656707" className="text-blue-400 font-semibold hover:underline">
                928-565-6707
              </a>
              .
            </p>
          )}

          <div className="text-center mt-10">
            <Link
              to="/internet-kingman-az"
              onClick={() => window.scrollTo(0, 0)}
              className="text-blue-400 font-semibold hover:underline"
            >
              View full Kingman internet page →
            </Link>
          </div>
        </div>
      </section>

      <PlanConnectModal open={!!connectPlan} plan={connectPlan} onClose={() => setConnectPlan(null)} />
    </>
  );
};

export default HomeKingmanPlans;
